import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Image } from "@/features/studio-ai/types/studio-ai.types"

import { deleteJob } from "@/api/studio-ai-api"
import {
  prefetchImageThumbnails,
  useImageThumbnailCache,
} from "@/features/studio-ai/hooks/use-image-thumbnail-prefetch"
import { getApiErrorMessage } from "@/lib/api-error-message"
import { ImageCacheService } from "@/services/image-cache-service"

/** Is the URL one the <a href download> link can fetch without auth headers? */
function isDownloadSafe(url: string | null | undefined): url is string {
  if (!url) return false
  return url.startsWith("data:") || url.startsWith("blob:")
}

export function useImageModal(options?: { onRefresh?: () => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.gallery" })
  const [selectedImage, setSelectedImage] = useState<Image | null>(null)
  const [modalOriginal, setModalOriginal] = useState<Image | null>(null)
  const [modalEdits, setModalEdits] = useState<Image[]>([])
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)

  const handleView = useCallback((image: Image & { _edits?: Image[] }) => {
    setSelectedImage(image)
    setModalOriginal(image)
    setModalEdits(image._edits || [])
    setIsImageModalOpen(true)
  }, [])

  const handleImageModalOpenChange = useCallback((open: boolean) => {
    setIsImageModalOpen(open)
    if (!open) {
      setSelectedImage(null)
      setModalOriginal(null)
      setModalEdits([])
    }
  }, [])

  const handleDownloadImage = useCallback(
    async (image: Image) => {
      // <a href download> can only fetch the URL using the browser's normal
      // navigation pipeline — no Authorization header. /api/studio-ai/images/<id>
      // 401s in any context where the EasyAuth cookie doesn't reach. Resolve
      // through the batch-prefetch cache, which already has a blob: URL the
      // download link can hit. For freshly-generated images that still live as
      // data: URLs in ImageCacheService, use those directly.
      const legacy = image.image_url || ImageCacheService.getImage(image.job_id)
      let downloadUrl: string | null = isDownloadSafe(legacy) ? legacy : null

      if (!downloadUrl) {
        const cache = useImageThumbnailCache.getState()
        if (cache.has(image.job_id)) {
          downloadUrl = cache.get(image.job_id) ?? null
        } else {
          // Cold path — fire a single-id batch and await it before linking.
          await prefetchImageThumbnails([image.job_id])
          downloadUrl = useImageThumbnailCache.getState().get(image.job_id) ?? null
        }
      }

      if (!downloadUrl) {
        toast.error(t("imageUrlNotAvailable"))
        return
      }

      // Pick the file extension from the blob's actual MIME instead of
      // hardcoding .png — the stored format can be jpeg, and forcing .png
      // gives the user jpeg bytes with a misleading extension that confuses
      // some viewers. Fetching a blob:/data: URL is synchronous-ish and
      // doesn't hit the network, so the small async hop is fine.
      let extension = "png"
      try {
        const probe = await fetch(downloadUrl)
        const blob = await probe.blob()
        if (blob.type === "image/jpeg") extension = "jpg"
        else if (blob.type === "image/webp") extension = "webp"
      } catch {
        // Worst case we fall back to .png. The download still works.
      }

      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = `studio-ai-${image.job_id}.${extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    [t]
  )

  const handleDeleteImage = useCallback(
    async (image: Image) => {
      try {
        await deleteJob(image.job_id)
        ImageCacheService.removeImage(image.job_id)
        useImageThumbnailCache.getState().removeUrl(image.job_id)
        toast.success(t("imageDeleted"))
        setIsImageModalOpen(false)
        setSelectedImage(null)
        setModalOriginal(null)
        setModalEdits([])
        options?.onRefresh?.()
      } catch (error) {
        toast.error(t("failedToDeleteImage"), {
          description: getApiErrorMessage(error) || undefined,
        })
      }
    },
    [t, options?.onRefresh]
  )

  return {
    selectedImage,
    setSelectedImage,
    modalOriginal,
    modalEdits,
    isImageModalOpen,
    handleView,
    handleImageModalOpenChange,
    handleDownloadImage,
    handleDeleteImage,
  }
}
